/**
 * Salaries endpoints (bos-api, BOS-109).
 *
 * Paths are relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/salaries/employees` reaches
 * `…/api/v1/ledger/salaries/employees`. This mirrors `AccountsEndpoints`,
 * `SettingsEndpoints` and `FixedAssetsEndpoints`, the other live ledger
 * resources.
 *
 * **`DELETE` removes only what nothing hangs off** (BOS-110). An employee who
 * has ever been *charged* by a run is refused with `EMPLOYEE_HAS_RUNS` and there
 * is no force flag: their line is what a posted entry was built from, and a
 * register that could lose them would leave the ledger charging somebody it no
 * longer has. Ending an employment is still `PUT` with a termination date.
 *
 * Only **paid** lines are stored, so an employee the register has never charged
 * — a mistyped new hire, someone who left before the first run — has no lines
 * and can be removed cleanly.
 *
 * A run is addressed by its **journal entry id** — a run has no identity apart
 * from the entry it created.
 */

/** Ids are server-shaped, but escaping keeps the path well-formed. */
const seg = (value: string): string => encodeURIComponent(value);

export type EmployeeListQueryParams = {
    /** `ALL` | `ACTIVE` | `TERMINATED`. Binds case-insensitively. */
    status?: string;
    /** Free text over the localized names **and** the personal number. */
    search?: string;
};

const query = ({ status, search }: EmployeeListQueryParams): string => {
    const params = new URLSearchParams();
    if (status) {
        params.set('status', status);
    }
    // A blank search is omitted rather than sent empty: the two mean the same
    // thing to the server, and leaving it out keeps the URL (and the query key
    // it mirrors) readable.
    if (search && search.trim() !== '') {
        params.set('search', search.trim());
    }
    const search_ = params.toString();
    return search_ ? `?${search_}` : '';
};

export const SalariesEndpoints = {
    /** GET — the employee register, with summary totals over the whole register. */
    employees: (options: EmployeeListQueryParams = {}) =>
        `/ledger/salaries/employees${query(options)}`,

    /** POST — create an employee. Idempotent on the client id; 201 then 200. */
    createEmployee: () => '/ledger/salaries/employees',

    /** PUT — replace every field, including dating the termination. */
    updateEmployee: (employeeId: string) =>
        `/ledger/salaries/employees/${seg(employeeId)}`,

    /** DELETE — remove an employee no run has ever charged. `204`, or `400 EMPLOYEE_HAS_RUNS`. */
    deleteEmployee: (employeeId: string) =>
        `/ledger/salaries/employees/${seg(employeeId)}`,

    /** GET — committed runs, newest period first. */
    payrollRuns: () => '/ledger/salaries/payroll-runs',

    /** GET — one run's stored per-employee breakdown, by **journal entry id**. */
    payrollRun: (journalEntryId: string) =>
        `/ledger/salaries/payroll-runs/${seg(journalEntryId)}`,

    /** POST — per-employee amounts + the draft a commit would build. Writes nothing. */
    previewRun: () => '/ledger/salaries/payroll-runs/preview',

    /** POST — create the period's DRAFT entry and record the run. Posts nothing. */
    commitRun: () => '/ledger/salaries/payroll-runs/commit',
};
